import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from '../config/axios'
import { useAuthStore } from '../store/authStore'
import { FiMail, FiUser, FiPhone, FiBriefcase, FiAlertCircle } from 'react-icons/fi'
import { FaWhatsapp } from 'react-icons/fa'
import { buildAuthWhatsAppURL } from '../config/whatsapp'
import PinInput from '../components/PinInput'

function AuthPage() {
  const navigate = useNavigate()
  const { login, register, error, clearError } = useAuthStore()

  const [isLogin, setIsLogin] = useState(true)
  const [isForgotPin, setIsForgotPin] = useState(false)
  const [loading, setLoading] = useState(false)
  const [emailOrCell, setEmailOrCell] = useState('')
  const [email, setEmail] = useState('')
  const [pin, setPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [cellNumber, setCellNumber] = useState('')
  const [localError, setLocalError] = useState('')
  const [resetEmail, setResetEmail] = useState('')
  const [resetSent, setResetSent] = useState(false)
  const [lockedUntil, setLockedUntil] = useState(null) // Date object
  const [countdown, setCountdown] = useState('')

  useEffect(() => {
    if (!lockedUntil) { setCountdown(''); return }
    const tick = () => {
      const diff = lockedUntil - Date.now()
      if (diff <= 0) { setLockedUntil(null); setCountdown(''); return }
      const m = Math.floor(diff / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      setCountdown(`${m}:${s.toString().padStart(2, '0')}`)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [lockedUntil])

  const handleForgotPin = async (e) => {
    e.preventDefault()
    setLocalError('')
    if (!resetEmail) { setLocalError('Please enter your email address'); return }
    setLoading(true)
    try {
      await axios.post('/api/auth/forgot-pin', { email: resetEmail })
      setResetSent(true)
    } catch (err) {
      setLocalError('Could not send reset email. Please check the address and try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLocalError('')
    clearError()

    if (isLogin) {
      if (!emailOrCell || pin.length < 6) {
        setLocalError('Please enter your email or cell number and your 6-digit PIN')
        return
      }
      setLoading(true)
      try {
        await login(emailOrCell, pin)
        navigate('/')
      } catch (err) {
        const msg = err.message || ''
        if (msg.startsWith('LOCKED:')) {
          setLockedUntil(new Date(msg.replace('LOCKED:', '')))
        } else {
          setLocalError(err.response?.data?.error || msg)
        }
        setPin('')
      } finally {
        setLoading(false)
      }
    } else {
      if (!firstName || !lastName || !companyName || !cellNumber || !email || pin.length < 6 || confirmPin.length < 6) {
        setLocalError('Please fill in all fields')
        return
      }
      if (pin !== confirmPin) {
        setLocalError('PINs do not match')
        return
      }
      setLoading(true)
      try {
        await register(email, pin, companyName, cellNumber, firstName, lastName)
        navigate('/')
      } catch (err) {
        setLocalError(err.message)
      } finally {
        setLoading(false)
      }
    }
  }

  const switchMode = () => {
    setIsLogin(!isLogin)
    setIsForgotPin(false)
    setResetSent(false)
    setLocalError('')
    setPin('')
    setConfirmPin('')
    clearError()
  }

  const displayError = localError || error

  return (
    <main className="min-h-screen bg-bg-dark flex items-center justify-center px-4">
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-accent-yellow mb-2">Case Sizing Engine</h1>
          <p className="text-accent-yellow">Input your specs, we do the work. You get the perfect case!</p>
        </div>

        {/* Auth Card */}
        <div className="card">
          {displayError && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-800 rounded flex gap-3 text-red-200">
              <FiAlertCircle className="flex-shrink-0 mt-0.5" />
              <p className="text-sm">{displayError}</p>
            </div>
          )}

          <h2 className="text-2xl font-bold text-white mb-6">
            {isLogin ? 'Sign In' : 'Create Account'}
          </h2>

          {isForgotPin ? (
            <form onSubmit={handleForgotPin} className="space-y-5">
              {resetSent ? (
                <div className="text-center space-y-4">
                  <div className="text-4xl">📧</div>
                  <p className="text-white font-semibold">Reset link sent</p>
                  <p className="text-gray-400 text-sm">Check your email inbox and click the link to reset your PIN.</p>
                  <button type="button" onClick={() => { setIsForgotPin(false); setResetSent(false); setResetEmail('') }} className="w-full btn-primary mt-2">
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-gray-400 text-sm">Enter your email address and we'll send you a link to reset your PIN.</p>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-accent-yellow">Email Address</label>
                    <div className="relative">
                      <FiMail className="absolute left-3 top-3 text-gray-500" />
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={e => setResetEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="input-field pl-10"
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <button type="submit" disabled={loading} className="w-full btn-primary mt-2 disabled:opacity-50 disabled:cursor-not-allowed">
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                  <button type="button" onClick={() => { setIsForgotPin(false); setLocalError('') }} className="w-full text-sm text-gray-500 hover:text-gray-300 transition mt-1">
                    Back to Sign In
                  </button>
                </>
              )}
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">

            {isLogin ? (
              <>
                {/* Email or cell */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-accent-yellow">Email or Cell Number</label>
                  <div className="relative">
                    <FiUser className="absolute left-3 top-3 text-gray-500" />
                    <input
                      type="text"
                      value={emailOrCell}
                      onChange={e => setEmailOrCell(e.target.value)}
                      placeholder="your@email.com or +27..."
                      className="input-field pl-10"
                      autoFocus
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* PIN */}
                <div>
                  <label className="block text-sm font-semibold mb-3 text-accent-yellow text-center">6-Digit PIN</label>
                  <PinInput value={pin} onChange={setPin} disabled={loading} />
                </div>
              </>
            ) : (
              <>
                {/* Name & Surname */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-accent-yellow">First Name</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      placeholder="John"
                      className="input-field"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-accent-yellow">Last Name</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      placeholder="Smith"
                      className="input-field"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-accent-yellow">Company Name</label>
                  <div className="relative">
                    <FiBriefcase className="absolute left-3 top-3 text-gray-500" />
                    <input
                      type="text"
                      value={companyName}
                      onChange={e => setCompanyName(e.target.value)}
                      placeholder="Your company"
                      className="input-field pl-10"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Cell */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-accent-yellow">Cell Number</label>
                  <div className="relative">
                    <FiPhone className="absolute left-3 top-3 text-gray-500" />
                    <input
                      type="tel"
                      value={cellNumber}
                      onChange={e => setCellNumber(e.target.value)}
                      placeholder="+27..."
                      className="input-field pl-10"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-accent-yellow">Email Address</label>
                  <div className="relative">
                    <FiMail className="absolute left-3 top-3 text-gray-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="input-field pl-10"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* PIN */}
                <div>
                  <label className="block text-sm font-semibold mb-3 text-accent-yellow text-center">Choose a 6-Digit PIN</label>
                  <PinInput value={pin} onChange={setPin} disabled={loading} />
                </div>

                {/* Confirm PIN */}
                <div>
                  <label className="block text-sm font-semibold mb-3 text-accent-yellow text-center">Confirm PIN</label>
                  <PinInput value={confirmPin} onChange={setConfirmPin} disabled={loading} />
                </div>
              </>
            )}

            {lockedUntil && (
              <div className="p-4 bg-red-900/20 border border-red-800 rounded text-center space-y-1">
                <p className="text-red-300 font-semibold text-sm">Account temporarily locked</p>
                <p className="text-red-400 text-xs">Too many failed attempts. Try again in</p>
                <p className="text-red-200 text-2xl font-mono font-bold">{countdown}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !!lockedUntil}
              className="w-full btn-primary mt-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  {isLogin ? 'Signing In…' : 'Creating Account…'}
                </>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>
            {loading && (
              <p className="text-center text-xs text-gray-500 animate-pulse">
                Please wait, this may take a few seconds…
              </p>
            )}

            {isLogin && (
              <div className="text-center mt-1">
                <button type="button" onClick={() => { setIsForgotPin(true); setLocalError(''); clearError() }} className="text-xs text-gray-500 hover:text-gray-300 transition">
                  Forgot PIN?
                </button>
              </div>
            )}
          </form>
          )}

          {/* Contact buttons */}
          <div className="mt-6 pt-5 border-t border-border-dark">
            <p className="text-center text-xs text-gray-500 mb-3">Need access or have a question?</p>
            <div className="flex justify-center">
              <a
                href={buildAuthWhatsAppURL()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] text-sm hover:bg-[#25D366]/20 transition"
              >
                <FaWhatsapp size={16} />
                WhatsApp Us
              </a>
            </div>
          </div>

          {/* Toggle */}
          {!isForgotPin && (
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
              <button onClick={switchMode} className="text-accent-yellow hover:text-orange-400 font-semibold transition">
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
          )}

        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-8">© 2026 - Case Sizing Engine</p>
      </div>
    </main>
  )
}

export default AuthPage
